// Heat Map — a player's shot/pass locations aggregated on the pitch, ported from
// js/renderers-heatmap.js. The first consumer of the React EventMapKit (04l): the kit's
// pitch markings, competition/season state, the event-filter bar, and the stats bar.
//
// needsPlayer:true, not mergeable. The comp/season selects go into main.js's strip
// (<EventSubControls>); the `.event-stats-bar` is a sibling before the body. The heat
// layer is plain JSX over heatmap-view.ts (the legacy rebuilds it as innerHTML per render,
// no transitions) — d3 is only the pure math behind the marks. In the snapshot only Katie
// McCabe has event data, so every other player gets the legacy's empty state with the link
// to her.
//
// Two sources, one body (as the Shot / Pass Map shell): the snapshot, or on ?live=1 any
// player's real shots + passes (T2-8, hooks/useLiveEventMap.ts).
//
// Instance state (same keys as the legacy, so saveState/undo capture them): _heatmapAction
// ('passOrigins'), _heatmapStyle ('contour'), _heatmapFilters (its own filter object — not
// Shot/Pass Map's shared _eventFilters), plus the kit's _eventMapCompetitionId/_eventMapSeason.

import { focusEmptyText, inFocus, moduleFocus } from "../live/event-focus.js";
import { FocusChip } from "./FocusChip.js";
import { ModuleExplainer } from "./ModuleExplainer.js";
import { eventChipsOf, explainHeatMap } from "./explain.js";
import { humanizeSeasonCode } from "./league-table-view.js";
import { useEffect, useMemo, useState } from "react";
import type { ProtoShotPassDetail } from "@catbento/shared";
import { type ProtoModuleProps, getModState, setModState } from "./bridge.js";
import { liveShortName, useRefresh, type EventMapData } from "./EventMapBody.js";
import { StyledSelect, type StyledSelectItem } from "./StyledSelect.js";
import { EventSubControls } from "./EventSubControls.js";
import { EventStatsBar } from "./EventStatsBar.js";
import { EventFilterBar } from "./EventFilterBar.js";
import { PitchMarkings } from "./PitchMarkings.js";
import { noDataMessage } from "./no-data.js";
import {
  HEATMAP_ACTIONS,
  HEATMAP_STYLES,
  resolveHeatmapAction,
  actionEvents,
  heatmapFilters,
  heatmapPoints,
  plottedText,
  gridCells,
  hexCells,
  contourPaths,
} from "./heatmap-view.js";
import {
  PITCH_L,
  PITCH_W,
  resolveEventState,
  matchesEventFilters,
  pruneStaleEventFilters,
  type EventActive,
} from "../engines/event-map-kit.js";
import { readSnapshot } from "../data/snapshot.js";
import { getPlayerShortName } from "../data/player-utils.js";
import { useEventContext } from "../hooks/useEventContext.js";
import { useShotPassEvents } from "../hooks/useShotPassEvents.js";
import { useLiveEventMap } from "../hooks/useLiveEventMap.js";
import { EVENT_KINDS } from "../live/event-map.js";
import { LIVE } from "../live/flag.js";

const ACTION_ITEMS: StyledSelectItem[] = HEATMAP_ACTIONS.map((a) => ({ value: a.key, label: a.label }));
const STYLE_ITEMS: StyledSelectItem[] = HEATMAP_STYLES.map((s) => ({ value: s.key, label: s.label }));

/** Hookless dispatch, so each source keeps its own hook order. Live: any player in the DB,
 *  by id. Snapshot: an unknown name → the first player (renderers-heatmap.js:292), so the
 *  body's hooks always see a real player. */
export function HeatMapModule(props: ProtoModuleProps) {
  if (LIVE) return <LiveHeatMap {...props} />;
  const players = readSnapshot().players;
  const name = props.playerName && players[props.playerName] ? props.playerName : Object.keys(players)[0];
  const p = name ? players[name] : undefined;
  if (!p || !name) return <div className="stats-empty">No data.</div>;
  return <SnapshotHeatMap {...props} eventsId={Number(p.id)} shortName={getPlayerShortName(p)} emptySeason={p.currentSeason ?? null} />;
}

/** `eventsId` = the shown player's id (her events are keyed by it), after the fallback. */
function SnapshotHeatMap(props: ProtoModuleProps & { eventsId: number; shortName: string; emptySeason: string | null }) {
  const { mod } = props;
  // The pickers' re-render must reach this parent, which resolves the pick (EventMapBody).
  const refresh = useRefresh();
  const ctx = useEventContext();
  const spd: ProtoShotPassDetail | null = useShotPassEvents(props.eventsId);
  // Resolve comp/season against THIS player's data, then write the resolved ids back —
  // the legacy resolveEventState did that write inside itself.
  const state = resolveEventState(
    {
      compId: getModState<string>(mod, "_eventMapCompetitionId"),
      seasonCode: getModState<string>(mod, "_eventMapSeason"),
    },
    spd,
  );
  if (state) {
    setModState(mod, "_eventMapCompetitionId", state.compId);
    setModState(mod, "_eventMapSeason", state.seasonCode);
  }
  return <HeatMapBody {...props} data={{ spd, state, ctx }} refresh={refresh} />;
}

/** Both shots and passes: the Action picker switches between them (and "All Actions" mixes
 *  them) without asking again. */
function LiveHeatMap(props: ProtoModuleProps) {
  const refresh = useRefresh();
  const data = useLiveEventMap(props.mod, props.playerId, EVENT_KINDS.both);
  return <HeatMapBody {...props} shortName={liveShortName(props)} data={data} refresh={refresh} />;
}

function HeatMapBody({
  mod,
  body,
  playerId,
  shortName,
  emptySeason,
  data: { spd, state, ctx },
  refresh: forceRender,
}: ProtoModuleProps & { shortName: string; data: EventMapData; refresh: () => void;
  /** The season the "no data" message names when she has no events at all (snapshot: her
   *  current one). Live: unknown → the message stops after her name. */
  emptySeason?: string | null }) {
  const [filterVersion, setFilterVersion] = useState(0);
  const bumpFilters = () => setFilterVersion((v) => v + 1);

  useEffect(() => {
    body.style.justifyContent = "flex-start";
  }, [body]);

  // Defaults are only seeded once there IS data (the legacy seeds them after its no-data
  // bail-out), so an empty-state module captures nothing extra.
  if (state) {
    if (!getModState<string>(mod, "_heatmapAction")) setModState(mod, "_heatmapAction", "passOrigins");
    if (!getModState<string>(mod, "_heatmapStyle")) setModState(mod, "_heatmapStyle", "contour");
    if (!getModState<EventActive>(mod, "_heatmapFilters")) setModState(mod, "_heatmapFilters", {});
  }
  const action = resolveHeatmapAction(getModState<string>(mod, "_heatmapAction"));
  const styleKey = getModState<string>(mod, "_heatmapStyle");
  const active = getModState<EventActive>(mod, "_heatmapFilters");

  const defs = useMemo(() => heatmapFilters(action, ctx), [action, ctx]);
  const season = state?.season;
  const seasonEvents = useMemo(() => (season ? actionEvents(action, season) : []), [action, season]);
  // Live: a match dragged in from Matches narrows the map to that game (Ben, 2026-10-18; event-focus.ts).
  const focus = LIVE ? moduleFocus(mod, playerId) : null;
  const rawEvents = useMemo(() => (focus ? seasonEvents.filter((e) => inFocus(e, focus)) : seasonEvents), [seasonEvents, focus]);
  const focusChip = focus ? <FocusChip mod={mod} focus={focus} /> : null;
  const focusChipInline = focus ? <FocusChip mod={mod} focus={focus} inline /> : null;

  // Self-heal the chips against the whole season's events (not one match's), in place, every render.
  if (active && seasonEvents.length) pruneStaleEventFilters(active, seasonEvents, defs);

  // Chip edits mutate `active` in place, so the version bump is what re-runs the filter +
  // the (KDE-heavy) layer build — not every unrelated re-render.
  const filtered = useMemo(
    () => (active ? rawEvents.filter((e) => matchesEventFilters(e, active, defs)) : []),
    // filterVersion stands in for the in-place edits to `active`.
    [rawEvents, active, defs, filterVersion],
  );
  const layer = useMemo(() => {
    const points = heatmapPoints(action, filtered);
    if (!points.length) return null;
    if (styleKey === "grid") {
      return (
        <g className="heatmap-grid" filter="url(#heatmap-blur)">
          {gridCells(points).map((c, i) => (
            <rect key={i} x={c.x} y={c.y} width={c.size} height={c.size} fill={c.fill} />
          ))}
        </g>
      );
    }
    if (styleKey === "hex") {
      return (
        <g className="heatmap-hexes">
          {hexCells(points).map((h, i) => (
            <polygon key={i} points={h.points} fill={h.fill} />
          ))}
        </g>
      );
    }
    const paths = contourPaths(points, action.contourBandwidth);
    return paths.length ? (
      <g className="heatmap-contours">
        {paths.map((c, i) => (
          <path key={i} d={c.d} fill={c.fill} />
        ))}
      </g>
    ) : null;
  }, [action, filtered, styleKey]);

  const subControls = (
    <EventSubControls
      mod={mod}
      spd={spd}
      state={state}
      onCompetitionChange={(compId) => {
        setModState(mod, "_eventMapCompetitionId", compId);
        // Deleted, not set to undefined — as the legacy does, so saveState captures no key.
        Reflect.deleteProperty(mod, "_eventMapSeason");
        forceRender();
      }}
      onSeasonChange={(seasonCode) => {
        setModState(mod, "_eventMapSeason", seasonCode);
        forceRender();
      }}
    />
  );

  if (!spd || !state || !active) {
    return (
      <>
        {subControls}
        <EventStatsBar mod={mod} body={body} hidden />
        {/* Ben's one message (Q-012). No season: she has no events in any. */}
        <div className="stats-empty">{noDataMessage("shot / pass", shortName, state?.seasonCode ?? emptySeason ?? null)}</div>
      </>
    );
  }

  if (!rawEvents.length) {
    return (
      <>
        {subControls}
        <EventStatsBar mod={mod} body={body} hidden />
        {focus && seasonEvents.length ? (
          <>
            {focusChip}
            <div className="stats-empty">{focusEmptyText(shortName, action.pluralNoun, focus)}</div>
          </>
        ) : (
          <div className="stats-empty">{noDataMessage(action.noun, shortName, state.seasonCode)}</div>
        )}
      </>
    );
  }

  return (
    <>
      {LIVE && (
        <ModuleExplainer
          mod={mod}
          paragraphs={explainHeatMap({
            name: shortName,
            competition: state.comp.name,
            season: humanizeSeasonCode(state.seasonCode),
            total: rawEvents.length,
            shown: filtered.length,
            chips: eventChipsOf(active, defs),
            focus,
            action: action.label,
            noun: action.pluralNoun,
          })}
        />
      )}
      {subControls}
      <EventStatsBar mod={mod} body={body} hidden={false}>
        <div className="event-stat">
          <span className="event-stat-value">{plottedText(filtered.length, rawEvents.length)}</span>
          <span className="event-stat-label">{action.pluralNoun} plotted</span>
        </div>
      </EventStatsBar>
      <div className="heatmap-wrap">
        <div className="heatmap-controls">
          <label className="heatmap-control">
            <span>Action</span>
            <StyledSelect
              items={ACTION_ITEMS}
              value={action.key}
              onChange={(v) => {
                setModState(mod, "_heatmapAction", v);
                forceRender();
              }}
              wrapClass="heatmap-action-select-wrap"
              extraClass="heatmap-action-select"
            />
          </label>
          <label className="heatmap-control">
            <span>Style</span>
            <StyledSelect
              items={STYLE_ITEMS}
              value={styleKey ?? "contour"}
              onChange={(v) => {
                setModState(mod, "_heatmapStyle", v);
                forceRender();
              }}
              wrapClass="heatmap-style-select-wrap"
              extraClass="heatmap-style-select"
            />
          </label>
        </div>
        <EventFilterBar active={active} events={rawEvents} defs={defs} onChange={bumpFilters} extra={focusChipInline} />
        {/* A variant of the kit's pitch shell on purpose: unrotated, and the heat layer is
            painted BEFORE the pitch frame so the markings stay legible on top of it. */}
        <div className="event-pitch-wrap">
          <svg className="event-pitch-svg heatmap-svg" viewBox={`0 0 ${PITCH_L} ${PITCH_W}`} preserveAspectRatio="xMidYMid meet">
            <defs className="event-defs">
              <filter id="heatmap-blur" x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur stdDeviation="2.2"></feGaussianBlur>
              </filter>
            </defs>
            <g className="heatmap-layer">{layer}</g>
            <g className="pitch-frame">
              <PitchMarkings />
            </g>
          </svg>
        </div>
      </div>
    </>
  );
}
